import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * FilterDropdown Component
 *
 * A dropdown selector for filtering data, with single or multi-select support.
 *
 * @example
 * ```jsx
 * // Single select
 * <FilterDropdown
 *   label="Status"
 *   value={statusFilter}
 *   options={[
 *     { value: 'all', label: 'All Status' },
 *     { value: 'open', label: 'Open' },
 *     { value: 'closed', label: 'Closed' },
 *   ]}
 *   onChange={setStatusFilter}
 * />
 *
 * // Multi-select
 * <FilterDropdown
 *   label="Categories"
 *   value={selectedCategories}
 *   options={categoryOptions}
 *   onChange={setSelectedCategories}
 *   multiple
 * />
 *
 * // With icon
 * <FilterDropdown
 *   label="Priority"
 *   icon={FlagIcon}
 *   value={priority}
 *   options={priorityOptions}
 *   onChange={setPriority}
 * />
 * ```
 */

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const FilterDropdown = ({
	label,
	placeholder,
	value,
	options = [],
	onChange,
	multiple = false,
	multiSelectLabel,
	icon: Icon,
	clearable = true,
	disabled = false,
	size = 'md',
	variant = 'default',
	className = '',
	dropdownClassName = '',
	position = 'left',
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Get selected option(s) for display
	const getDisplayValue = () => {
		if (multiple && Array.isArray(value)) {
			if (value.length === 0) return placeholder || label;
			if (value.length === 1) {
				const option = options.find((opt) => opt.value === value[0]);
				return option?.label || value[0];
			}
			return multiSelectLabel || `${value.length} selected`;
		}

		if (!value || value === 'all') return placeholder || label;
		const option = options.find((opt) => opt.value === value);
		return option?.label || value;
	};

	const isSelected = (optionValue) => {
		if (multiple && Array.isArray(value)) {
			return value.includes(optionValue);
		}
		return value === optionValue;
	};

	const handleSelect = (optionValue) => {
		if (multiple) {
			const newValue = Array.isArray(value) ? [...value] : [];
			if (newValue.includes(optionValue)) {
				onChange(newValue.filter((v) => v !== optionValue));
			} else {
				onChange([...newValue, optionValue]);
			}
		} else {
			onChange(optionValue);
			setIsOpen(false);
		}
	};

	const handleClear = (e) => {
		e.stopPropagation();
		onChange(multiple ? [] : 'all');
	};

	const hasValue = multiple
		? Array.isArray(value) && value.length > 0
		: value && value !== 'all';

	const sizeClasses = {
		sm: 'px-2.5 py-1.5 text-xs',
		md: 'px-3 py-2 text-sm',
		lg: 'px-4 py-2.5 text-base',
	};

	const variantClasses = {
		default: hasValue
			? 'border-indigo-300 text-indigo-700 bg-indigo-50'
			: 'border-gray-200 text-gray-700 bg-white',
		outline: hasValue
			? 'border-indigo-500 text-indigo-700 bg-white'
			: 'border-gray-300 text-gray-700 bg-white',
		ghost: hasValue
			? 'border-transparent text-indigo-700 bg-indigo-50'
			: 'border-transparent text-gray-600 bg-gray-50',
	};

	const positionClasses = {
		left: 'left-0',
		right: 'right-0',
		center: 'left-1/2 -translate-x-1/2',
	};

	// Sort options alphabetically, keeping 'all' and 'uncategorized' at the top
	const sortedOptions = [...options].sort((a, b) => {
		if (a.value === 'all') return -1;
		if (b.value === 'all') return 1;
		if (a.value === 'uncategorized') return -1;
		if (b.value === 'uncategorized') return 1;
		return (a.label || '').localeCompare(b.label || '');
	});

	return (
		<div className={classNames('relative', className)} ref={dropdownRef}>
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					if (!disabled) setIsOpen(!isOpen);
				}}
				disabled={disabled}
				className={classNames(
					'inline-flex items-center gap-2 border rounded-lg transition-colors',
					sizeClasses[size] || sizeClasses.md,
					variantClasses[variant] || variantClasses.default,
					disabled
						? 'opacity-50 cursor-not-allowed'
						: 'hover:bg-gray-50 cursor-pointer',
				)}
			>
				{Icon && <Icon className="w-4 h-4 text-gray-400" />}
				<span className="truncate max-w-[150px]">{getDisplayValue()}</span>
				{clearable && hasValue ? (
					<XMarkIcon
						className="w-4 h-4 text-gray-400 hover:text-gray-600"
						onClick={handleClear}
					/>
				) : (
					<ChevronDownIcon
						className={classNames(
							'w-4 h-4 text-gray-400 transition-transform',
							isOpen ? 'rotate-180' : '',
						)}
					/>
				)}
			</button>

			{isOpen && (
				<div
					className={classNames(
						'absolute mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 max-h-64 overflow-auto',
						positionClasses[position] || positionClasses.left,
						dropdownClassName,
					)}
					onClick={(e) => e.stopPropagation()}
				>
					{sortedOptions.map((option) => (
						<button
							type="button"
							key={option.value}
							onClick={(e) => {
								e.stopPropagation();
								handleSelect(option.value);
							}}
							className={classNames(
								'flex items-center justify-between w-full px-4 py-2 text-left text-sm transition-colors',
								isSelected(option.value)
									? 'text-indigo-700 bg-indigo-50'
									: 'text-gray-700 hover:bg-gray-50',
								option.disabled ? 'opacity-50 cursor-not-allowed' : '',
							)}
							disabled={option.disabled}
						>
							<div className="flex items-center gap-2">
								{option.icon && (
									<option.icon className="w-4 h-4 text-gray-400" />
								)}
								{option.color && (
									<span
										className="w-2.5 h-2.5 rounded-full"
										style={{ backgroundColor: option.color }}
									/>
								)}
								<span>{option.label}</span>
							</div>
							{isSelected(option.value) && (
								<CheckIcon className="w-4 h-4 text-indigo-600" />
							)}
						</button>
					))}

					{sortedOptions.length === 0 && (
						<div className="px-4 py-3 text-sm text-gray-500 text-center">
							No options available
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default FilterDropdown;
